import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { SearchReport, SynonymGroup } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Site içi arama (F5B-03): en çok aranan ve sonuçsuz kalan aramalar; eş anlamlı grupları ("mama" = "yem").
 * Rapor kişi bilgisi tutmaz; e-posta veya telefon numarası içeren aramalar kaydedilmez.
 */

const DAYS = [7, 30, 90] as const;

export function SiteSearchPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <PageFrame helpKey="page.site_search">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {storeId ? <ReportSection storeId={storeId} /> : <Spinner label={t('common.loading')} />}
      <SynonymsSection />
    </PageFrame>
  );
}

function QueryTable({ rows, label }: { rows: SearchReport['top']; label: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={label}>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.siteSearch.colQuery')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.siteSearch.colSearches')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.siteSearch.colResults')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.query} className="border-t border-line">
              <td className="px-3 py-2">{r.query}</td>
              <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.searches)}</td>
              <td className="px-3 py-2 text-end tabular-nums">
                {r.lastResultCount === 0 ? (
                  <Badge tone="amber">{t('admin.siteSearch.noResults')}</Badge>
                ) : (
                  formatNumber(r.lastResultCount)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const [days, setDays] = useState<(typeof DAYS)[number]>(30);
  const q = useQuery({
    queryKey: ['search-report', storeId, days],
    queryFn: () => api.get<SearchReport>(`/v1/admin/search/report?storeId=${storeId}&days=${days}`),
  });
  const d = q.data;
  return (
    <Section
      title={t('admin.siteSearch.reportTitle')}
      description={t('admin.siteSearch.reportLead')}
      action={<HelpTip k="site_search.report" />}
    >
      <Field label={t('admin.siteSearch.period')} className="mb-4 max-w-xs">
        {(p) => (
          <Select
            {...p}
            value={days}
            onChange={(e) => setDays(Number(e.target.value) as (typeof DAYS)[number])}
          >
            {DAYS.map((x) => (
              <option key={x} value={x}>
                {t('admin.siteSearch.lastDays', { days: x })}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : d.totalSearches === 0 ? (
        <EmptyState title={t('admin.siteSearch.emptyTitle')} description={t('admin.siteSearch.emptyLead')} />
      ) : (
        <div className="space-y-5">
          <dl className="grid gap-3 sm:grid-cols-3">
            {(
              [
                ['totalSearches', d.totalSearches],
                ['distinctQueries', d.distinctQueries],
                ['zeroResultSearches', d.zeroResultSearches],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="rounded-md border border-line px-4 py-3">
                <dt className="text-sm text-ink-2">{t(`admin.siteSearch.${k}`)}</dt>
                <dd className="text-xl font-semibold tabular-nums">{formatNumber(v)}</dd>
              </div>
            ))}
          </dl>
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-2">
              <h3 className="font-medium">{t('admin.siteSearch.topTitle')}</h3>
              <QueryTable rows={d.top} label={t('admin.siteSearch.topTitle')} />
            </div>
            <div className="space-y-2">
              <h3 className="flex items-center gap-1 font-medium">
                {t('admin.siteSearch.zeroTitle')} <HelpTip k="site_search.zero" />
              </h3>
              {d.zeroResults.length ? (
                <QueryTable rows={d.zeroResults} label={t('admin.siteSearch.zeroTitle')} />
              ) : (
                <p className="text-sm text-ink-3">{t('admin.siteSearch.noZero')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function SynonymsSection() {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('seo.write');
  const [editing, setEditing] = useState<SynonymGroup | 'new' | null>(null);
  const q = useQuery({
    queryKey: ['search-synonyms'],
    queryFn: () => api.get<SynonymGroup[]>('/v1/admin/search/synonyms'),
  });
  const del = useApiMutation((id: string) => api.del(`/v1/admin/search/synonyms/${id}`), {
    invalidate: [['search-synonyms']],
    success: t('admin.siteSearch.synonymDeleted'),
  });
  return (
    <Section
      title={t('admin.siteSearch.synonymsTitle')}
      description={t('admin.siteSearch.synonymsLead')}
      action={
        <span className="flex items-center gap-1">
          {canWrite && (
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              {t('admin.siteSearch.addSynonym')}
            </Button>
          )}
          <HelpTip k="site_search.synonyms" />
        </span>
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-ink-3">{t('admin.siteSearch.noSynonyms')}</p>
      ) : (
        <ul
          className="divide-y divide-line rounded-md border border-line"
          aria-label={t('admin.siteSearch.synonymsTitle')}
        >
          {q.data.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span className="flex flex-wrap gap-1">
                {g.terms.map((term) => (
                  <Badge key={term} tone="neutral">
                    {term}
                  </Badge>
                ))}
              </span>
              {canWrite && (
                <span className="inline-flex gap-1">
                  <Button
                    size="sm"
                    onClick={() => setEditing(g)}
                    aria-label={t('admin.siteSearch.editSynonymItem', { terms: g.terms.join(', ') })}
                  >
                    {t('common.edit')}
                  </Button>
                  <ConfirmButton
                    variant="ghost"
                    message={t('admin.siteSearch.deleteSynonymConfirm', { terms: g.terms.join(', ') })}
                    onConfirm={() => del.mutateAsync(g.id)}
                  >
                    {t('common.delete')}
                  </ConfirmButton>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && <SynonymDialog row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function SynonymDialog({ row, onClose }: { row: SynonymGroup | null; onClose: () => void }) {
  const t = useT();
  const [text, setText] = useState((row?.terms ?? []).join(', '));
  const terms = text
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
  const save = useApiMutation(
    () =>
      row
        ? api.put(`/v1/admin/search/synonyms/${row.id}`, { terms, version: row.version })
        : api.post('/v1/admin/search/synonyms', { terms }),
    {
      invalidate: [['search-synonyms']],
      success: t('admin.siteSearch.synonymSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={row ? t('admin.siteSearch.editSynonym') : t('admin.siteSearch.addSynonym')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.siteSearch.terms')}
        required
        help={<HelpTip k="site_search.terms" />}
        error={fieldError(save.error, 'terms')}
      >
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={text}
            placeholder={t('admin.siteSearch.termsPlaceholder')}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </Field>
      {save.error && !fieldError(save.error, 'terms') && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {save.error.title}
        </p>
      )}
    </Dialog>
  );
}
